import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  useOrders,
  useStartUnwinder,
  useStopUnwinder,
  useUnwinderState,
  useUnwindingRules
} from '../../api/useUnwinding';
import { useUpdates } from 'src/Modules/Arbitrage/context/updatesContext';

const OPEN_ORDER_STATUSES = new Set(['resting', 'live', 'partial', 'pending']);

export const StopStartUnwinderButtons = () => {
  const { data: state } = useUnwinderState();
  const { data: rules } = useUnwindingRules();
  const { data: ordersData } = useOrders();
  const stopMutation = useStopUnwinder();
  const startMutation = useStartUnwinder();

  const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
  const [startConfirmOpen, setStartConfirmOpen] = useState(false);

  const activeRuleIds = useMemo(
    () =>
      new Set(
        Array.isArray(rules)
          ? rules.filter(r => r.status === 'active').map(r => r._id)
          : []
      ),
    [rules]
  );
  const activeRuleCount = activeRuleIds.size;
  const pausedRuleCount = useMemo(
    () =>
      Array.isArray(rules) ? rules.filter(r => r.status === 'paused').length : 0,
    [rules]
  );
  const { openOrdersFromActiveRules, openOrdersFromPausedRules } = useMemo(() => {
    const openOrders = (ordersData?.orders ?? []).filter(o =>
      OPEN_ORDER_STATUSES.has(o.status)
    );
    return {
      openOrdersFromActiveRules: openOrders.filter(
        o => o.ruleId && activeRuleIds.has(o.ruleId)
      ).length,
      openOrdersFromPausedRules: openOrders.filter(
        o => !o.ruleId || !activeRuleIds.has(o.ruleId)
      ).length,
    };
  }, [ordersData, activeRuleIds]);

  const isRunning = state?.isRunning ?? true;
  const isBusy = stopMutation.isLoading || startMutation.isLoading;
  // Panic only fires when active rules exist. Open orders tied to paused rules
  // are intentionally left alone (user-managed), so they don't block the stop.
  const willTriggerPanic = activeRuleCount > 0;

  // Shared system/job stepper — only show entries from the unwinder subsystem
  const { currentJob } = useUpdates();
  const isMyJob = Boolean(
    currentJob &&
      currentJob.subsystem === 'unwinder' &&
      (currentJob.status === 'running' ||
        (currentJob.completedAt && Date.now() - currentJob.completedAt < 5000))
  );

  // Keep the stepper visible for ~4s after completion, then hide
  const [showJobResult, setShowJobResult] = useState(false);
  const prevJobRunning = useRef(false);
  useEffect(() => {
    if (isMyJob) {
      prevJobRunning.current = true;
      setShowJobResult(true);
    } else if (prevJobRunning.current && !isMyJob) {
      prevJobRunning.current = false;
      const timer = setTimeout(() => setShowJobResult(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isMyJob]);

  const jobProgress = useMemo(() => {
    if (!currentJob || !isMyJob) return 0;
    const completed = currentJob.steps.filter(s => s.status === 'completed').length;
    return Math.round((completed / currentJob.steps.length) * 100);
  }, [currentJob, isMyJob]);
  const showProgress = (isMyJob || showJobResult) && currentJob;

  const handleConfirmStop = () => {
    stopMutation.mutate();
    setStopConfirmOpen(false);
  };

  const handleConfirmStart = () => {
    startMutation.mutate();
    setStartConfirmOpen(false);
  };

  const stopRunning =
    isMyJob && currentJob?.type === 'stop' && currentJob?.status === 'running';
  const startRunning =
    isMyJob && currentJob?.type === 'start' && currentJob?.status === 'running';

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip
            title={
              stopRunning
                ? 'Stopping unwinder…'
                : !isRunning
                  ? 'Unwinder already stopped'
                  : 'Stop position-unwinder instance'
            }
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={
                  stopMutation.isLoading || stopRunning ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <PowerSettingsNewIcon />
                  )
                }
                onClick={() => setStopConfirmOpen(true)}
                disabled={isBusy || !isRunning || Boolean(isMyJob && currentJob?.status === 'running')}
                sx={{ fontSize: '0.7rem' }}
              >
                {stopRunning ? 'Stopping' : 'Stop Unwinder'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              startRunning
                ? 'Starting unwinder…'
                : isRunning
                  ? 'Unwinder already running'
                  : 'Start position-unwinder instance'
            }
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                color="success"
                size="small"
                startIcon={
                  startMutation.isLoading || startRunning ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                onClick={() => setStartConfirmOpen(true)}
                disabled={isBusy || isRunning || Boolean(isMyJob && currentJob?.status === 'running')}
                sx={{ fontSize: '0.7rem' }}
              >
                {startRunning ? 'Starting' : 'Start Unwinder'}
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {/* Progress popover */}
        {showProgress && currentJob && (
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              right: 0,
              mt: 1,
              zIndex: 1400
            }}
          >
            <Paper elevation={6} sx={{ p: 1.5, minWidth: 280, maxWidth: 340, bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {currentJob.type === 'start' ? 'Starting' : 'Stopping'} Unwinder
                </Typography>
                {currentJob.status === 'completed' && (
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                )}
                {currentJob.status === 'failed' && (
                  <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
                )}
              </Box>

              {currentJob.status === 'running' && (
                <LinearProgress
                  variant="determinate"
                  value={jobProgress}
                  sx={{ mb: 1, borderRadius: 1, height: 3 }}
                />
              )}

              <Stepper
                activeStep={currentJob.currentStep}
                orientation="vertical"
                sx={{
                  '& .MuiStepConnector-line': { minHeight: 8 },
                  '& .MuiStepLabel-label': { fontSize: '0.7rem' },
                  '& .MuiStepLabel-iconContainer': { pr: 1 },
                  '& .MuiSvgIcon-root': { fontSize: '0.9rem' }
                }}
              >
                {currentJob.steps.map((step, idx) => (
                  <Step key={idx} completed={step.status === 'completed'}>
                    <StepLabel error={step.status === 'failed'} sx={{ py: 0 }}>
                      {step.label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              {currentJob.error && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {currentJob.error}
                </Typography>
              )}
            </Paper>
          </Box>
        )}
      </Box>

      {/* Stop confirmation */}
      <Dialog
        open={stopConfirmOpen}
        onClose={() => setStopConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem', pb: 0.5 }}>Stop Position Unwinder</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'grey.400' }}>
              This scales the <code>position-unwinder</code> deployment to 0 replicas.
              It is separate from the global Predictions on/off and from PANIC.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mt: 0.5 }}>
              <Typography sx={{ fontSize: '0.8rem' }}>
                <b>Rules to pause:</b> {activeRuleCount}
                {pausedRuleCount > 0 && (
                  <Typography component="span" sx={{ fontSize: '0.8rem', color: 'grey.500' }}>
                    {' '}({pausedRuleCount} already paused)
                  </Typography>
                )}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem' }}>
                <b>Orders to cancel:</b> {openOrdersFromActiveRules}
                <Typography component="span" sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
                  {' '}(from active rules)
                </Typography>
              </Typography>
              <Typography sx={{ fontSize: '0.8rem' }}>
                <b>Orders to remain open:</b> {openOrdersFromPausedRules}
                <Typography component="span" sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
                  {' '}(unrelated to active rules)
                </Typography>
              </Typography>
            </Box>
            {willTriggerPanic ? (
              <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
                <b>PANIC</b> will fire to pause {activeRuleCount} rule
                {activeRuleCount === 1 ? '' : 's'} and cancel {openOrdersFromActiveRules} order
                {openOrdersFromActiveRules === 1 ? '' : 's'}, then the unwinder will stop.
                {openOrdersFromPausedRules > 0 && (
                  <> {openOrdersFromPausedRules} order{openOrdersFromPausedRules === 1 ? '' : 's'} not tied to active rules will remain open.</>
                )}
              </Alert>
            ) : (
              <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                No active rules. Panic will be skipped; unwinder will stop directly.
                {openOrdersFromPausedRules > 0 && (
                  <> {openOrdersFromPausedRules} open order{openOrdersFromPausedRules === 1 ? '' : 's'} not tied to active rules will remain open.</>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopConfirmOpen(false)} size="small">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStop}
            variant="contained"
            color="warning"
            size="small"
            disabled={stopMutation.isLoading}
          >
            {stopMutation.isLoading ? <CircularProgress size={14} color="inherit" /> : 'Stop Unwinder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start confirmation */}
      <Dialog
        open={startConfirmOpen}
        onClose={() => setStartConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem', pb: 0.5 }}>Start Position Unwinder</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.8rem', color: 'grey.400' }}>
            Scale <code>position-unwinder</code> back to 1 replica.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartConfirmOpen(false)} size="small">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStart}
            variant="contained"
            color="success"
            size="small"
            disabled={startMutation.isLoading}
          >
            {startMutation.isLoading ? <CircularProgress size={14} color="inherit" /> : 'Start Unwinder'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
